import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '120px 60px',
  background: '#faf9f7',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    right: '-100px',
    transform: 'translateY(-50%)',
    width: '400px',
    height: '400px',
    background: 'radial-gradient(circle, rgba(201, 162, 39, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '900px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '56px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(201, 162, 39, 0.1)',
  border: '1px solid rgba(201, 162, 39, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#c9a227',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(32px, 5vw, 48px)',
  fontWeight: 400,
  color: '#1a1a1a',
  lineHeight: 1.2,
  letterSpacing: '-1px',
  marginBottom: '16px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #c9a227 0%, #a67c52 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const StatusBadge = styled(Box)(({ isOpen }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px 24px',
  background: isOpen ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
  border: `1px solid ${isOpen ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
  borderRadius: '50px',
  marginTop: '8px'
}));

const StatusDot = styled(Box)(({ isOpen }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  background: isOpen ? '#22c55e' : '#ef4444',
  boxShadow: isOpen ? '0 0 12px #22c55e' : '0 0 12px #ef4444',
  animation: 'pulse 2s infinite'
}));

const StatusText = styled(Typography)(({ isOpen }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: isOpen ? '#22c55e' : '#ef4444'
}));

const TableCard = styled(Box)({
  background: '#ffffff',
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: '0 10px 50px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: '0.1s',
  animationFillMode: 'backwards'
});

const TableHeader = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 2fr 80px',
  padding: '20px 32px',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr 1.5fr 60px',
    padding: '16px 20px'
  }
});

const TableHeaderCell = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'uppercase',
  letterSpacing: '1.5px'
});

const TableBody = styled(Box)({});

const TableRow = styled(Box)(({ isToday, index }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 2fr 80px',
  padding: '20px 32px',
  alignItems: 'center',
  background: isToday ? 'linear-gradient(135deg, rgba(201, 162, 39, 0.08) 0%, rgba(201, 162, 39, 0.03) 100%)' : 'transparent',
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  animation: `${fadeInUp} 0.5s ease-out`,
  animationDelay: `${index * 0.05}s`,
  animationFillMode: 'backwards',
  '&:last-child': {
    borderBottom: 'none'
  },
  '&:hover': {
    background: isToday
      ? 'linear-gradient(135deg, rgba(201, 162, 39, 0.12) 0%, rgba(201, 162, 39, 0.05) 100%)'
      : 'rgba(0, 0, 0, 0.02)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr 1.5fr 60px',
    padding: '16px 20px'
  }
}));

const DayCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
});

const DayName = styled(Typography)(({ isToday }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: isToday ? 700 : 500,
  color: isToday ? '#c9a227' : '#1a1a1a'
}));

const TodayBadge = styled(Box)({
  padding: '4px 10px',
  background: 'linear-gradient(135deg, #c9a227 0%, #e8d5a3 100%)',
  borderRadius: '20px',
  fontFamily: "'Inter', sans-serif",
  fontSize: '10px',
  fontWeight: 700,
  color: '#1a1a1a',
  textTransform: 'uppercase',
  letterSpacing: '1px'
});

const TimeCell = styled(Typography)(({ isClosed }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 500,
  color: isClosed ? '#999999' : '#444444',
  '@media (max-width: 600px)': {
    fontSize: '13px'
  }
}));

const StatusCell = styled(Box)({
  display: 'flex',
  justifyContent: 'center'
});

const StatusIcon = styled(Box)(({ isOpen }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: isOpen ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
  '& svg': {
    fontSize: '18px',
    color: isOpen ? '#22c55e' : '#ef4444'
  }
}));

const defaultHours = [
  { day: 'Monday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Tuesday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Wednesday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Thursday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Friday', open: '9:00 AM', close: '6:00 PM', isClosed: false },
  { day: 'Saturday', open: '10:00 AM', close: '4:00 PM', isClosed: false },
  { day: 'Sunday', open: '', close: '', isClosed: true }
];

const dayMapping = {
  'Mon': 'Monday',
  'Tue': 'Tuesday',
  'Wed': 'Wednesday',
  'Thu': 'Thursday',
  'Fri': 'Friday',
  'Sat': 'Saturday',
  'Sun': 'Sunday'
};

const BusinessHoursSection = ({ businessHours, contactInfo }) => {
  const today = new Date().getDay();
  const adjustedToday = today === 0 ? 6 : today - 1;

  const hours = businessHours?.map(h => ({
    ...h,
    day: dayMapping[h.day?.substring(0, 3)] || h.day
  })) || defaultHours;

  const isCurrentlyOpen = () => {
    const currentDay = hours[adjustedToday];
    return !currentDay?.isClosed;
  };

  return (
    <SectionContainer id="hours">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <AccessTimeIcon sx={{ fontSize: 16, color: '#c9a227' }} />
            <BadgeText>Opening Hours</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Business <GradientText>Hours</GradientText>
          </SectionTitle>
          <StatusBadge isOpen={isCurrentlyOpen()}>
            <StatusDot isOpen={isCurrentlyOpen()} />
            <StatusText isOpen={isCurrentlyOpen()}>
              {isCurrentlyOpen() ? 'We are Open Now' : 'Currently Closed'}
            </StatusText>
          </StatusBadge>
        </SectionHeader>

        <TableCard>
          <TableHeader>
            <TableHeaderCell>Day</TableHeaderCell>
            <TableHeaderCell>Hours</TableHeaderCell>
            <TableHeaderCell sx={{ textAlign: 'center' }}>Status</TableHeaderCell>
          </TableHeader>

          <TableBody>
            {hours.map((dayInfo, index) => {
              const isToday = index === adjustedToday;
              const isOpen = !dayInfo.isClosed;
              // Use hoursText for split hours, or fallback to open-close format
              const displayHours = dayInfo.hoursText || (dayInfo.isClosed ? 'Closed' : `${dayInfo.open} - ${dayInfo.close}`);

              return (
                <TableRow key={dayInfo.day} isToday={isToday} index={index}>
                  <DayCell>
                    <DayName isToday={isToday}>{dayInfo.day}</DayName>
                    {isToday && <TodayBadge>Today</TodayBadge>}
                  </DayCell>

                  <TimeCell isClosed={dayInfo.isClosed}>
                    {displayHours}
                  </TimeCell>

                  <StatusCell>
                    <StatusIcon isOpen={isOpen}>
                      {isOpen ? <CheckCircleIcon /> : <CancelIcon />}
                    </StatusIcon>
                  </StatusCell>
                </TableRow>
              );
            })}
          </TableBody>
        </TableCard>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default BusinessHoursSection;
